import { useEffect, useRef } from 'react';

/**
 * Re-run `callback` every `intervalMs` while the browser tab is visible.
 *
 * Keeps the dashboard auto-synced without the admin needing to hit refresh.
 * Pauses when the tab is hidden and resumes on focus to avoid wasting
 * cycles on background tabs. Skips overlapping runs by tracking an
 * in-flight flag so slow requests never stampede the server.
 *
 * Usage:
 *   const { pulse } = useAutoRefresh(fetchRows, 5000);
 *   <LiveDot animating={pulse} />
 *
 * @param {Function} callback  async function to call on each tick
 * @param {number}   intervalMs  ms between ticks (default 5000)
 * @param {boolean}  enabled  pause polling when false (default true)
 */
export function useAutoRefresh(callback, intervalMs = 5000, enabled = true) {
  const savedCallback = useRef(callback);
  const inFlight = useRef(false);

  // Keep the latest callback without resubscribing the interval.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs) return;

    let alive = true;
    let timer = null;

    const tick = async () => {
      if (!alive) return;
      if (document.hidden) return;           // skip while tab hidden
      if (inFlight.current) return;          // skip overlapping runs
      inFlight.current = true;
      try { await savedCallback.current?.(); }
      catch { /* keep polling even on errors */ }
      finally { inFlight.current = false; }
    };

    timer = setInterval(tick, intervalMs);

    // Force an immediate refresh whenever the tab becomes visible again
    // so the admin sees fresh data the instant they come back.
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      alive = false;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs, enabled]);
}

export default useAutoRefresh;
